import { useState } from "react";

import { isEmpty, times } from "lodash/fp";

import ScrollSlider from "@app/analytics/components/ScrollSlider";
import useRecordingInfo from "@app/analytics/hooks/recordings/useRecordingInfo";
import useRecordingStatus, {
  RecordingStatus,
} from "@app/analytics/hooks/recordings/useRecordingStatus";
import * as Events from "@app/analytics/tracking/events";
import { CoachingMoment } from "@app/common/types";
import { UnauthorizedVideoRecording, VideoRecording } from "@app/common/types";

import VideoMoment, {
  EmptyVideoMoment,
  LoadingVideoMoment,
  VideoMomentProps,
} from "./VideoMoment";

type CoachingMomentType =
  | "filler_words"
  | "wpm"
  | "non_inclusive_terms"
  | "interruptions";

export interface CoachingMomentProps {
  getMomentLabel: (moment: CoachingMoment) => string;
  isLoading: boolean;
  moments: CoachingMoment[];
  type: CoachingMomentType;
}

const MIN_CLIP_DURATION = 2;

// offset in seconds for each type to ensure we're capturing enough initial context of the moment
const START_TIME_OFFSET_BY_TYPE: { [key in CoachingMomentType]?: number } = {
  interruptions: 2,
};

const getClip = (
  moment: CoachingMoment,
  type: CoachingMomentProps["type"],
  recordingInfo?: VideoRecording | UnauthorizedVideoRecording
) => {
  if (
    !moment.session ||
    !recordingInfo ||
    recordingInfo.status === "unauthorized"
  ) {
    return;
  }

  const videoOffset = Math.max(
    recordingInfo.startTime.diff(moment.session.startTime).as("seconds"),
    0
  );

  const momentVideoStartTime = moment.startTime.minus({ seconds: videoOffset });
  const momentVideoEndTime = moment.endTime.minus({ seconds: videoOffset });

  const clipStartOffset = START_TIME_OFFSET_BY_TYPE[type] ?? 0;
  const minDuration = MIN_CLIP_DURATION + clipStartOffset;

  const clipStart = momentVideoStartTime.minus({ seconds: clipStartOffset });

  const startSeconds = clipStart.diff(recordingInfo.startTime).as("seconds");
  const endSeconds = Math.max(
    momentVideoEndTime.diff(recordingInfo.startTime).as("seconds"),
    startSeconds + minDuration
  );
  const duration = Math.max(endSeconds - startSeconds, minDuration);

  return {
    id: moment.id,
    start: startSeconds,
    end: endSeconds,
    duration,
  };
};

const getStateFromRecordingStatus = (status: RecordingStatus) => {
  switch (status) {
    case "unauthorized":
    case "unsupported_browser":
      return "locked";
    case "no_video":
      return "unavailable";
    default:
      return "available";
  }
};

const ConnectedVideoMoment = ({
  forcePause,
  label,
  moment,
  onPlay,
  type,
}: {
  forcePause: boolean;
  label: string;
  moment: CoachingMoment;
  onPlay: VideoMomentProps["onPlay"];
  type: CoachingMomentProps["type"];
}) => {
  const { recordingInfo, hasRecording, isLoading } = useRecordingInfo(
    moment.sessionId
  );
  const { status } = useRecordingStatus({ recordingInfo, isLoading });

  return (
    <VideoMoment
      clip={getClip(moment, type, recordingInfo)}
      date={moment.startTime.toFormat("M/d/yy")}
      forcePause={forcePause}
      label={label}
      loading={isLoading}
      onPlay={onPlay}
      recordingInfo={
        hasRecording ? (recordingInfo as VideoRecording) : undefined
      }
      state={getStateFromRecordingStatus(status)}
      title={moment.session?.title ?? ""}
      thumbnailUrl={moment.session?.thumbnailUrl}
    />
  );
};

const CoachingMoments = ({
  getMomentLabel,
  isLoading,
  moments,
  type,
}: CoachingMomentProps) => {
  // keep track of which moment is currently playing so that the user can't get into a state
  // of having multiple moments playing at once
  const [playingMomentId, setPlayingMomentId] = useState<
    CoachingMoment["id"] | null
  >(null);

  // list of moment ids we've captured as "played" in mixpanel (order irrelevant)
  const [capturedMomentIds, setCapturedMomentIds] = useState<string[]>([]);

  if (isLoading) {
    return (
      <ScrollSlider>
        {times(
          (i) => (
            <LoadingVideoMoment key={i} />
          ),
          6
        )}
      </ScrollSlider>
    );
  }

  if (isEmpty(moments)) {
    return (
      <ScrollSlider>
        <EmptyVideoMoment />
      </ScrollSlider>
    );
  }

  const handlePlayMoment = (clipId: string) => {
    setPlayingMomentId(clipId);
    if (!capturedMomentIds.includes(clipId)) {
      Events.playCoachingMoment({ type, id: clipId });
      setCapturedMomentIds((state) => [...state, clipId]);
    }
  };

  return (
    <ScrollSlider>
      {moments.map((moment) => (
        <ConnectedVideoMoment
          key={moment.id}
          forcePause={playingMomentId != null && playingMomentId != moment.id}
          label={getMomentLabel(moment)}
          moment={moment}
          onPlay={handlePlayMoment}
          type={type}
        />
      ))}
    </ScrollSlider>
  );
};

export default CoachingMoments;
